/**
 * PDF de las fichas de pago generadas: una ficha por hoja, para imprimir o repartir.
 *
 * Es un módulo de DATOS (arma el docDefinition de pdfmake); no importa pdfmake ni React, así
 * que se puede renderizar y revisar con un `node script.js`.
 *
 * @param {object}   args
 * @param {Array}    args.fichas    resultados con estado "creada"
 * @param {object}   args.escuelas  { [escuelaId]: { nombre, instrucciones, bancos[] } }
 * @param {object}   args.empresa   { nombre, logo (data URL) }
 */

const COLOR_PRINCIPAL = "#007A8A";
const COLOR_SUAVE = "#E6F7F9";
const COLOR_TEXTO_SUAVE = "#595959";

const dinero = (valor) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(valor) || 0);

// "2026-11-15" -> "15/11/2026" sin pasar por Date: no hay zona horaria que lo mueva de día.
const fechaLegible = (fechaDia) => {
  const partes = String(fechaDia || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return partes ? `${partes[3]}/${partes[2]}/${partes[1]}` : "";
};

const hoy = () => {
  const fecha = new Date();
  const dos = (n) => String(n).padStart(2, "0");
  return `${dos(fecha.getDate())}/${dos(fecha.getMonth() + 1)}/${fecha.getFullYear()}`;
};

// pdfkit sólo abre PNG y JPEG; cualquier otro logo (SVG, WebP) tumbaría el PDF completo.
const logoUsable = (logo) => typeof logo === "string" && /^data:image\/(png|jpe?g);base64,/i.test(logo);

/**
 * Columnas de la tabla de cuentas: sólo las que traiga al menos una cuenta. Encabezado,
 * anchos y renglones salen de esta misma lista, así que nunca se desalinean (una fila más
 * larga que `widths` tumba pdfmake).
 *
 * Cuenta, CLABE y tarjeta van con ancho `auto` y SIN espacios de ancho cero: no se pueden
 * partir, pero caben (18 dígitos ≈ 85 pt), y el papá las copia del PDF a la app de su banco;
 * un carácter invisible pegado ahí la haría rechazar.
 */
const columnasCuentas = (bancos) =>
  [
    { titulo: "Banco", ancho: "auto", valor: (b) => b.banco },
    { titulo: "Beneficiario", ancho: "*", valor: (b) => b.beneficiario },
    { titulo: "Cuenta", ancho: "auto", valor: (b) => b.cuenta },
    { titulo: "CLABE", ancho: "auto", valor: (b) => b.clabe },
    { titulo: "Tarjeta", ancho: "auto", valor: (b) => b.numeroTarjeta },
    { titulo: "Indicaciones", ancho: "*", valor: (b) => b.instrucciones },
  ].filter((columna, indice) => indice < 2 || bancos.some((banco) => String(columna.valor(banco) || "").trim()));

const tablaCuentas = (bancos) => {
  if (!bancos || bancos.length === 0) {
    return {
      text: "La escuela no tiene cuentas bancarias configuradas.",
      italics: true,
      color: COLOR_TEXTO_SUAVE,
      margin: [0, 4, 0, 0],
    };
  }

  const columnas = columnasCuentas(bancos);

  return {
    table: {
      headerRows: 1,
      // Un arreglo nuevo por tabla: pdfmake muta `widths` al medir.
      widths: columnas.map((columna) => columna.ancho),
      body: [
        columnas.map((columna) => ({ text: columna.titulo, style: "thCuenta" })),
        ...bancos.map((banco) => columnas.map((columna) => ({ text: columna.valor(banco) || "", style: "tdCuenta" }))),
      ],
    },
    layout: {
      hLineColor: () => "#D9D9D9",
      vLineColor: () => "#D9D9D9",
      fillColor: (fila) => (fila === 0 ? COLOR_SUAVE : null),
    },
  };
};

/**
 * Fichas de lista escolar: lo que se cobra, artículo por artículo. El importe no se muestra en
 * los artículos de una lista con precios ocultos (como en la tienda); si todos lo están, la
 * columna no se pinta.
 */
const tablaArticulos = (articulos) => {
  const conImporte = articulos.some((articulo) => !articulo.precioOculto);
  return {
    margin: [0, 10, 0, 0],
    table: {
      headerRows: 1,
      // Un arreglo nuevo por tabla: pdfmake muta `widths` al medir.
      widths: conImporte ? ["*", "auto", "auto"] : ["*", "auto"],
      body: [
        [
          { text: "Artículo", style: "thCuenta" },
          { text: "Cant.", style: "thCuenta", alignment: "center" },
          ...(conImporte ? [{ text: "Importe", style: "thCuenta", alignment: "right" }] : []),
        ],
        ...articulos.map((articulo) => [
          { text: articulo.descripcion || "", style: "tdCuenta" },
          { text: String(articulo.cantidad ?? ""), style: "tdCuenta", alignment: "center" },
          ...(conImporte
            ? [{ text: articulo.precioOculto ? "" : dinero(articulo.total), style: "tdCuenta", alignment: "right" }]
            : []),
        ]),
      ],
    },
    layout: {
      hLineColor: () => "#D9D9D9",
      vLineColor: () => "#D9D9D9",
      fillColor: (fila) => (fila === 0 ? COLOR_SUAVE : null),
    },
  };
};

const filaDato = (etiqueta, valor) => [
  { text: etiqueta, style: "etiqueta" },
  { text: valor || "—", style: "valor" },
];

const ficha = ({ resultado, escuela, empresa, conLogo, primera }) => {
  const nombreEscuela = escuela?.nombre || resultado.escuela || "";
  const limite = fechaLegible(resultado.fechaLimite);

  return [
    {
      ...(primera ? {} : { pageBreak: "before" }),
      columns: [
        ...(conLogo ? [{ image: "logo", fit: [110, 60], width: 120 }] : []),
        {
          width: "*",
          stack: [
            { text: empresa?.nombre || "", style: "empresa" },
            ...(nombreEscuela ? [{ text: nombreEscuela, style: "escuela" }] : []),
          ],
        },
        {
          width: "auto",
          stack: [
            { text: "FICHA DE PAGO", style: "tituloFicha" },
            { text: `Folio ${resultado.folio || ""}`, style: "folio" },
          ],
        },
      ],
      columnGap: 12,
    },
    {
      canvas: [{ type: "line", x1: 0, y1: 0, x2: 532, y2: 0, lineWidth: 1.5, lineColor: COLOR_PRINCIPAL }],
      margin: [0, 10, 0, 12],
    },
    {
      table: {
        widths: [90, "*"],
        body: [
          filaDato("Alumno", resultado.alumno),
          filaDato("Matrícula", resultado.matricula),
          ...(resultado.grupo ? [filaDato("Grupo", resultado.grupo)] : []),
          filaDato("Concepto", resultado.concepto),
          ...(resultado.periodo ? [filaDato("Periodo", resultado.periodo)] : []),
          ...(limite ? [filaDato("Fecha límite", limite)] : []),
        ],
      },
      layout: "noBorders",
    },
    ...(resultado.articulos?.length ? [tablaArticulos(resultado.articulos)] : []),
    {
      margin: [0, 16, 0, 16],
      table: {
        widths: ["*", "*"],
        body: [
          [
            {
              stack: [
                { text: "REFERENCIA", style: "etiquetaCaja" },
                { text: resultado.referencia || "", style: "referencia" },
              ],
              fillColor: COLOR_SUAVE,
              margin: [0, 10, 0, 10],
            },
            {
              stack: [
                { text: "TOTAL A PAGAR", style: "etiquetaCaja" },
                { text: dinero(resultado.totalPagar), style: "total" },
              ],
              fillColor: COLOR_SUAVE,
              margin: [0, 10, 0, 10],
            },
          ],
        ],
      },
      layout: {
        hLineColor: () => COLOR_PRINCIPAL,
        vLineColor: () => COLOR_PRINCIPAL,
        hLineWidth: () => 1.5,
        vLineWidth: () => 1.5,
      },
    },
    {
      text: [
        { text: "Cómo pagar: ", bold: true },
        "haz una transferencia por el total exacto a cualquiera de las cuentas de abajo y escribe la ",
        { text: "referencia", bold: true },
        " en el concepto o la referencia de la transferencia, para que tu pago se identifique.",
      ],
      style: "parrafo",
    },
    ...(escuela?.instrucciones ? [{ text: escuela.instrucciones, style: "parrafo", margin: [0, 6, 0, 0] }] : []),
    { text: "Cuentas para tu pago", style: "subtitulo" },
    tablaCuentas(escuela?.bancos || []),
    {
      text: `Generada el ${hoy()}. Conserva tu comprobante de transferencia.`,
      style: "pie",
      margin: [0, 18, 0, 0],
    },
  ];
};

const fichasPagoPdf = ({ fichas, escuelas, empresa }) => {
  const conLogo = logoUsable(empresa?.logo);
  const lista = fichas || [];

  const contenido =
    lista.length > 0
      ? lista.flatMap((resultado, indice) =>
          ficha({
            resultado,
            escuela: escuelas?.[resultado.escuelaId] || null,
            empresa,
            conLogo,
            primera: indice === 0,
          }),
        )
      : [{ text: "No hay fichas generadas.", italics: true }];

  return {
    pageSize: "LETTER",
    pageMargins: [40, 40, 40, 40],
    info: { title: "Fichas de pago", creator: "pdfmake" },
    // El logo una sola vez: referenciado por nombre, pdfmake lo incrusta una vez para todas
    // las hojas en lugar de repetirlo en cada ficha.
    ...(conLogo ? { images: { logo: empresa.logo } } : {}),
    content: contenido,
    defaultStyle: { fontSize: 10 },
    styles: {
      empresa: { fontSize: 13, bold: true },
      escuela: { fontSize: 10, color: COLOR_TEXTO_SUAVE, margin: [0, 2, 0, 0] },
      tituloFicha: { fontSize: 15, bold: true, color: COLOR_PRINCIPAL, alignment: "right" },
      folio: { fontSize: 10, color: COLOR_TEXTO_SUAVE, alignment: "right", margin: [0, 2, 0, 0] },
      etiqueta: { bold: true, color: COLOR_TEXTO_SUAVE, margin: [0, 2, 0, 2] },
      valor: { margin: [0, 2, 0, 2] },
      etiquetaCaja: { fontSize: 9, color: COLOR_TEXTO_SUAVE, alignment: "center", characterSpacing: 1 },
      referencia: { fontSize: 26, bold: true, alignment: "center", characterSpacing: 3, color: "#0B4F5A" },
      total: { fontSize: 22, bold: true, alignment: "center", color: "#0B4F5A" },
      parrafo: { fontSize: 10, lineHeight: 1.2 },
      subtitulo: { fontSize: 11, bold: true, color: COLOR_PRINCIPAL, margin: [0, 14, 0, 6] },
      thCuenta: { bold: true, fontSize: 9 },
      tdCuenta: { fontSize: 9 },
      pie: { fontSize: 8, color: COLOR_TEXTO_SUAVE, italics: true },
    },
  };
};

export default fichasPagoPdf;
